import { useQueryClient } from '@tanstack/react-query';
import { File02, File04, FileCheck02, Image01 } from '@untitledui/icons';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  selectSignature,
  useAccountSignatures,
} from '@/hooks/email/useAccountSignatures';
import { useConnectedAccounts } from '@/hooks/integrations/useConnectedAccounts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { optionalRecipients } from '@/lib/compose/EmailComposePanel';
import { filterFilesByAttachmentCap } from '@/lib/files/attachmentValidation';
import {
  MAX_EMAIL_ATTACHMENT_TOTAL_BYTES,
  MAX_EMAIL_ATTACHMENT_TOTAL_MB,
} from '@/lib/files/constants';
import { fileService, FileUploadError } from '@/lib/files/service';
import { useLatestRef } from '@/lib/hooks/useLatestRef';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import {
  type EmailContent,
  emailContentFromGmailSend,
} from '@/lib/proposals/core/emailContent';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import { proposalDetailQuery } from '@/lib/proposals/query/queries';
import {
  getStableActionKey,
  isSameLogicalAction,
} from '@/lib/proposals/utils/helpers';
import {
  appendSignatureBlock,
  reconcileSignatureDisposition,
} from '@/lib/proposals/utils/signatureDisposition';
import {
  categorizeNetworkError,
  categorizeUploadErrorBody,
} from '@/lib/reasoning/uploadErrors';

import type {
  GmailSendPayload,
  ProposalDetail,
  ProposedAction,
} from '../../../lib/proposals/core/types';
import type { EmailEditorHandle } from '../../emails/EmailEditor';
import EmailEditor from '../../emails/EmailEditor';
import { EmailPreviewModal } from '../../emails/preview/EmailPreviewModal';
import type { EmailPreviewPayload } from '../../emails/preview/useEmailPreview';
import { type UploadedFile } from '../../files/EnhancedFileUpload';
import {
  type LiveActionContent,
  useCoordinatedAutoSave,
  useEmailTracking,
} from '../shared';
import type { CoordinatedRetryPolicy } from '../shared/useCoordinatedAutoSave';

// Auto-save retry backoff. On a failed save we keep the user's local edits
// authoritative and keep retrying until the save lands, so a transient network
// or server blip never silently drops data. Exponential backoff capped so we
// never hammer the server: 2s, 4s, 8s, 16s, 30s, 30s, …
const AUTOSAVE_BASE_RETRY_DELAY_MS = 2000;
const AUTOSAVE_MAX_RETRY_DELAY_MS = 30000;

// Above this many failures, show one summary toast instead of one per file.
const MAX_INDIVIDUAL_FILE_TOASTS = 3;

// Error codes the update_payload endpoint returns for a PUT against an action
// id that is no longer the current version (see `update_payload` in
// apps/actions/views: `get_action` resolves current rows only, so a superseded
// id surfaces as a not-found ValueError wrapped in UPDATE_FAILED).
const STALE_ACTION_ERROR_CODES = new Set([
  'UPDATE_FAILED',
  'PROPOSAL_NOT_FOUND',
]);

/**
 * True when a 400 body carries one of the stale-action codes. Tolerates the
 * two shapes DRF emits for a dict-style ValidationError (`code: "X"` and
 * `code: ["X"]`) and returns false for anything unparseable.
 */
function isStaleActionErrorBody(body: string): boolean {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!parsed || typeof parsed !== 'object' || !('code' in parsed)) {
      return false;
    }
    const { code } = parsed as { code: unknown };
    const value = Array.isArray(code) ? code[0] : code;
    return typeof value === 'string' && STALE_ACTION_ERROR_CODES.has(value);
  } catch {
    return false;
  }
}

/**
 * Parses a `Retry-After` header value into a millisecond delay. Per RFC 9110
 * the value is either a non-negative integer number of seconds or an
 * HTTP-date. Returns undefined when the header is absent or unparseable, so
 * the caller can fall back to its own backoff.
 */
function parseRetryAfterMs(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (!header) {
    return undefined;
  }
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000;
  }
  const dateMs = Date.parse(header);
  if (Number.isNaN(dateMs)) {
    return undefined;
  }
  const delay = dateMs - Date.now();
  return delay > 0 ? delay : 0;
}

/**
 * A non-ok `update_payload` response, classified for the autosave retry policy.
 * `retryable` distinguishes TRANSIENT failures (5xx, 429, a stale action id we
 * just re-resolved) — worth retrying with backoff — from DETERMINISTIC ones
 * (other 4xx), where retrying would only hammer the server. Anything thrown
 * that is not this type is a network-level failure and is retried.
 */
class AutosaveRequestError extends Error {
  readonly retryable: boolean;
  readonly retryAfterMs?: number;

  constructor(message: string, retryable: boolean, retryAfterMs?: number) {
    super(message);
    this.name = 'AutosaveRequestError';
    this.retryable = retryable;
    this.retryAfterMs = retryAfterMs;
  }
}

interface EmailActionPreviewProps {
  action: ProposedAction<GmailSendPayload>;
  proposalId: number;
  editable?: boolean;
  onActionUpdate?: (updatedAction: ProposedAction<GmailSendPayload>) => void;
  isProcessing?: boolean;
  orgId?: number;
  /** Callback when user clicks "View Thread" - opens the thread panel */
  onOpenThread?: () => void;
  /** Number of versions for version history button */
  versionCount?: number;
  /** Callback when version history button is clicked */
  onShowVersionHistory?: () => void;
  /** Callback when files are attached via UI (e.g., attach button) */
  onAttachFile?: (files: File[]) => void;
  /**
   * When true, this email is the only action in the proposal, so the body
   * editor grows to its full height (no 300px cap) and the surrounding
   * proposal panel handles overflow. Defaults to false (cap stays) so every
   * existing multi-action call site is unchanged.
   */
  isSoleAction?: boolean;
}

export default function EmailActionPreview({
  action,
  proposalId,
  editable = false,
  onActionUpdate,
  isProcessing = false,
  orgId: orgIdProp,
  onOpenThread,
  versionCount,
  onShowVersionHistory,
  isSoleAction = false,
}: EmailActionPreviewProps) {
  const params = useParams<{ orgId: string }>();
  // Use explicit prop if provided, otherwise fall back to route param
  const orgId = orgIdProp?.toString() || params.orgId;

  // Edited content state - initialize from action payload
  const [editedContent, setEditedContent] = useState<EmailContent>(() =>
    emailContentFromGmailSend(action)
  );

  // The editor must NEVER be remounted while the user is editing — remounting
  // destroys DOM, focus, cursor, scroll position, and undo history. External
  // content is applied IN PLACE through this handle (see the content-sync
  // effect below); there is deliberately no remount key.
  const emailEditorRef = useRef<EmailEditorHandle>(null);

  // Server representation ("ack") of the last save WE dispatched. Incoming
  // action props whose content matches this are echoes of our own save
  // round-trip (parent state propagation, cache patch re-render, or a refetch
  // that landed post-save) and must never be re-applied to the editor.
  const lastAckedServerContentRef = useRef<string | null>(null);
  const lastAckedUpdatedAtRef = useRef<string | null>(null);

  // Serialized content the server is known to hold for this action: what our
  // last successful write sent, or the external content we last adopted. The
  // content-sync effect uses it to recognize echoes of our own saves. The
  // autosave hook keeps its own change-detection baseline; the two are always
  // advanced together (see persistContent / markServerBaseline).
  const lastPersistedContentRef = useRef<string>(
    JSON.stringify(emailContentFromGmailSend(action))
  );

  // External content that arrived while the user had unsaved edits, parked
  // until those edits settle (latest wins).
  const deferredExternalContentRef = useRef<{
    content: EmailContent;
    str: string;
    updatedAt?: string;
  } | null>(null);

  // The action id is NOT stable across saves. A save past the versioning
  // window (`_NEW_VERSION_AFTER_SECONDS`, 120s) mints a NEW action row with a
  // new id and marks the old row `is_current_version=False`; the backend's
  // `get_action` only resolves current versions, so reusing the old id makes
  // the NEXT save fail with "Action {id} not found" — surfaced to the user as
  // a generic UPDATE_FAILED 400.
  //
  // The `action` prop does eventually carry the new id, but relying on that
  // round-trip is not enough: a write queued before the new id propagates
  // would still target the dead row. Track the id in a ref, adopt the server's
  // row on every save, and read it when the write actually runs. Mirrors the
  // pattern in ReviewPermissionsRenderer / CreatePipelineRenderer /
  // OnboardingActionRenderer.
  const actionIdRef = useRef(action.id);
  useEffect(() => {
    actionIdRef.current = action.id;
  }, [action.id]);

  // The one identity that survives a version rollover is the version tree's
  // root: every row carries it as `parent_action` (the root row itself has
  // null there and IS the root). `execution_order` is NOT stable — the
  // repository reserves a tree's superseded orders when it mints a version,
  // which can push the new current row to a higher order than its history
  // (seen live: v1 at order 0, v2 and v3 at order 1). Matching a refetched
  // row by order therefore misses the very rollover we need to recover from.
  const treeRootRef = useRef(getStableActionKey(action));
  useEffect(() => {
    treeRootRef.current = getStableActionKey(action);
  }, [action]);

  const queryClient = useQueryClient();

  // Attached files state
  const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>([]);

  // Track file attachment upload in progress
  const [isAttaching, setIsAttaching] = useState(false);

  // Connected accounts for multi-account OAuth support
  const { accounts: connectedAccounts } = useConnectedAccounts({ orgId });

  // The "Track opens" toggle is rendered in the ActionAccordion header (left
  // of the Gmail badge) by `EmailHeaderRight` in MultiActionTimeline.tsx.
  // EmailActionPreview's only role here is to (a) initialize the tracking
  // context from the `email_pixel_tracking` feature flag and the action's
  // existing `disable_tracking` value, and (b) react to context changes by
  // syncing them into `editedContent` so they persist via autosave.
  const trackingEnabled = useFeatureFlag('email_pixel_tracking');
  const {
    initialize: initializeTracking,
    disableTracking: contextDisableTracking,
    isInitialized: trackingProviderInitialized,
  } = useEmailTracking();

  // Determine effective editable state - disable during AI processing
  const effectiveEditable = editable && !isProcessing;

  // Get attachment IDs from the CURRENT action's payload
  // Stabilize with useMemo to prevent recreating empty array on every render
  const attachmentFileIds = useMemo(
    () => action.payload.action_params.attachment_file_ids ?? [],
    [action.payload.action_params.attachment_file_ids]
  );
  const attachmentIdsKey = attachmentFileIds.join(','); // Stable string for dependency

  const fetchAttachedFiles = useCallback(
    async (signal: AbortSignal) => {
      if (!orgId) {
        setAttachedFiles([]);
        return;
      }

      if (attachmentFileIds.length === 0) {
        setAttachedFiles([]);
        return;
      }

      try {
        // Fetch each file by ID
        const filePromises = attachmentFileIds.map(async (fileId: string) => {
          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/files/${fileId}/`,
            { signal }
          );
          if (response.ok) {
            return await response.json();
          }
          return null;
        });

        const files = await Promise.all(filePromises);
        setAttachedFiles(files.filter(f => f !== null));
      } catch (error) {
        // Silently ignore AbortError (expected on unmount/id change)
        if (!(error instanceof Error && error.name === 'AbortError')) {
          console.error('Failed to fetch attached files:', error);
        }
      }
    },
    [orgId, attachmentFileIds]
  );

  // Fetch files when attachment IDs change
  useEffect(() => {
    const controller = new AbortController();
    fetchAttachedFiles(controller.signal);
    return () => controller.abort();
  }, [fetchAttachedFiles, attachmentIdsKey, orgId]);

  // Track action ID and version to prevent unnecessary resets
  const lastActionIdRef = useRef<number>(action.id);
  const lastVersionRef = useRef<number | undefined>(action.version_number);

  // Skip the draft → autosave sync on the mount render (the initial content is
  // the server's, not an edit).
  const isFirstRenderRef = useRef(true);

  // Commit-synced mirrors for reads made after an await or from outside
  // React's render (the approve footers' content getter, handlers).
  const editedContentRef = useLatestRef(editedContent);

  // Track context_message from the action payload so the stale-data hash the
  // backend computes matches what we echo back. The backend stores
  // `{action_params, context_message?}` at the payload top level; omitting
  // context_message here would make compute_actions_hash diverge and the
  // approve call would fail with a 409 STALE_ACTIONS.
  const contextMessageRef = useLatestRef(action.payload.context_message);

  // Track execution_order for stale-data matching (see LiveActionContent).
  const executionOrderRef = useLatestRef(action.execution_order);

  // Create a stable fingerprint of the action's email content for change detection
  // This ensures the effect fires even for in-place updates (same ID/version, different payload)
  // which occur when AI edits within 2 minutes of the last edit (backend time-based versioning)
  const contentFingerprint = useMemo(() => {
    const content = emailContentFromGmailSend(action);
    return JSON.stringify(content);
  }, [action]);

  // Patch the proposal detail cache in place with the PUT response so the
  // cache, the server, and our local baseline always agree after a save.
  // Without this, the cache diverges after every autosave and any later
  // refetch (AI-chat refresh, reconnect) delivers content that differs from
  // the editor — the historical cause of mid-edit overwrites. Matching by
  // version-tree root (parent_action ?? id) via isSameLogicalAction mirrors
  // the version-rollover handling in ActionVersionTimeline, with
  // execution_order+type as a fallback only for rows lacking parent_action.
  const patchProposalDetailCache = useCallback(
    (
      updatedAction: ProposedAction<GmailSendPayload>,
      proposalMeta?: { title?: string; updated_at?: string }
    ) => {
      if (!orgId) {
        return;
      }
      queryClient.setQueryData<ProposalDetail>(
        proposalQueryKeys.detail(Number(orgId), proposalId),
        old =>
          old && {
            ...old,
            title: proposalMeta?.title ?? old.title,
            updated_at: proposalMeta?.updated_at ?? old.updated_at,
            actions: old.actions.map(a =>
              isSameLogicalAction(a, updatedAction) ? updatedAction : a
            ),
          }
      );
    },
    [orgId, proposalId, queryClient]
  );

  // Record the server's representation of a save we dispatched so the content
  // sync effect can recognize its round-trip as an echo (see above).
  //
  // Monotonicity guard: overlapping in-flight saves can have their responses
  // land out of order. A straggling response from an older, since-superseded
  // save must not revert the ack refs backward once a newer save's response
  // has already been applied — that would let a stale refetch slip past the
  // stale-content guard below.
  const ackSavedAction = useCallback(
    (updatedAction: ProposedAction<GmailSendPayload>) => {
      // Monotonic: apply only when there's no acked updated_at yet, or the
      // incoming updated_at >= the currently acked updated_at.
      const incomingUpdatedAt = updatedAction.updated_at ?? null;
      const isOutOfOrderAck =
        lastAckedUpdatedAtRef.current !== null &&
        incomingUpdatedAt !== null &&
        incomingUpdatedAt < lastAckedUpdatedAtRef.current;

      if (isOutOfOrderAck) {
        logger.debug(
          '[EmailActionPreview] Ignoring out-of-order ack from a superseded save',
          {
            actionId: updatedAction.id,
            staleUpdatedAt: incomingUpdatedAt,
            ackedUpdatedAt: lastAckedUpdatedAtRef.current,
          }
        );
        return;
      }

      // Adopt the server's row so the NEXT save targets the current version.
      // This sits behind the same monotonicity guard on purpose: a straggling
      // response from a superseded save carries an id that is already stale,
      // and adopting it would send the following save to a dead row.
      actionIdRef.current = updatedAction.id;
      treeRootRef.current = getStableActionKey(updatedAction);

      lastAckedServerContentRef.current = JSON.stringify(
        emailContentFromGmailSend(updatedAction)
      );

      // A response with no `updated_at` must not reset an already-established
      // watermark to null — that would silently disable the monotonicity
      // guard above for every ack that follows, reopening the exact
      // out-of-order window this guard exists to close. The bootstrap case
      // (no ack yet, watermark already null) is unaffected: this branch only
      // fires when there's a real watermark to protect.
      if (
        incomingUpdatedAt === null &&
        lastAckedUpdatedAtRef.current !== null
      ) {
        logger.warn(
          '[EmailActionPreview] Ack response missing updated_at — watermark could not advance',
          {
            actionId: updatedAction.id,
            ackedUpdatedAt: lastAckedUpdatedAtRef.current,
          }
        );
      } else {
        lastAckedUpdatedAtRef.current = incomingUpdatedAt;
      }
    },
    []
  );

  // After a stale-id rejection, re-resolve the action's CURRENT row from the
  // server and adopt its id so the retry lands. The row is matched by its
  // version tree root (see `treeRootRef`); execution_order + type is only a
  // fallback for a row that carries no `parent_action`. Returns false when
  // there is nothing newer to adopt (the id we used is still the current one,
  // so the rejection had another cause — e.g. the proposal has left
  // `pending`), in which case the caller must not retry.
  const adoptCurrentActionId = async (staleId: number): Promise<boolean> => {
    if (!orgId) {
      return false;
    }
    try {
      const detail = await queryClient.fetchQuery({
        ...proposalDetailQuery(Number(orgId), proposalId),
        staleTime: 0,
      });
      const heldShape: ProposedAction = {
        ...action,
        id: staleId,
        parent_action: treeRootRef.current,
        execution_order: executionOrderRef.current,
      };
      const current = detail.actions.find(a =>
        isSameLogicalAction(a, heldShape)
      );
      if (!current || current.id === staleId) {
        return false;
      }
      logger.warn(
        '[EmailActionPreview] Autosave targeted a superseded action id — adopting current',
        { staleId, currentId: current.id, proposalId }
      );
      actionIdRef.current = current.id;
      treeRootRef.current = getStableActionKey(current);
      // Hand the row to the parent too, so the prop round-trip and the
      // version list catch up. Local edits are still pending, so the content
      // sync effect parks this row's content instead of applying it.
      onActionUpdate?.(current as ProposedAction<GmailSendPayload>);
      return true;
    } catch (error) {
      logger.warn(
        '[EmailActionPreview] Could not re-resolve the current action id',
        { staleId, proposalId, error }
      );
      return false;
    }
  };

  // The autosave write. The shared hook owns everything around it — debounce,
  // serialization, retry/backoff, freeze and decision-lock handling, and the
  // coordination state the approve footers and the unsaved-changes guard read.
  // It resolves on success and throws on failure; `sentContent` is exactly the
  // value being persisted (the user may keep typing while it is in flight).
  const persistContent = async (sentContent: EmailContent): Promise<void> => {
    if (!orgId) {
      return;
    }
    const sentContentStr = JSON.stringify(sentContent);
    // Snapshot the id we address, so a rejection can be checked against the
    // id that was actually used rather than one adopted later.
    const sentActionId = actionIdRef.current;

    let response: Response;
    try {
      response = await fetchWithDefaults(
        `/api/orgs/${orgId}/proposals/${proposalId}/actions/${sentActionId}/update_payload/`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            payload: {
              action_params: sentContent,
            },
          }),
        }
      );
    } catch (error) {
      logger.error(
        '[EmailActionPreview] Autosave threw',
        error instanceof Error ? error : undefined,
        {
          actionId: actionIdRef.current,
          proposalId,
        }
      );
      // A thrown fetch is a network-level failure — transient, so retried.
      throw error;
    }

    if (!response.ok) {
      // Capture diagnostics for the intermittent autosave failure. error()
      // always outputs to console and lands in the window.__ahoy.logs() ring
      // buffer for later inspection.
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        // Response body may be unavailable/already consumed — ignore.
      }
      logger.error(
        '[EmailActionPreview] Autosave failed (response not ok)',
        undefined,
        {
          actionId: sentActionId,
          proposalId,
          status: response.status,
          statusText: response.statusText,
          body: errorBody.slice(0, 500),
        }
      );
      const message = `Autosave failed (HTTP ${response.status} ${response.statusText})`;

      // A 400 carrying UPDATE_FAILED / PROPOSAL_NOT_FOUND is how the backend
      // reports a PUT against a retired version row (`get_action` only
      // resolves current versions). That is recoverable: re-resolve the
      // current row, adopt its id, and let the backoff retry re-send the
      // user's edits. Treating it as a dead-end 4xx is what parked the editor
      // in "Error saving" with the edits stranded client-side.
      if (
        response.status === 400 &&
        isStaleActionErrorBody(errorBody) &&
        (await adoptCurrentActionId(sentActionId))
      ) {
        throw new AutosaveRequestError(message, true);
      }

      // Retry only transient server failures (5xx) or rate limiting (429).
      // 4xx (validation/permission/conflict) is deterministic — surface it.
      // A 429 may carry a `Retry-After` header telling us exactly how long
      // to wait — honor it over our own guess when present.
      throw new AutosaveRequestError(
        message,
        response.status >= 500 || response.status === 429,
        response.status === 429 ? parseRetryAfterMs(response) : undefined
      );
    }

    // Get updated action from response (may be new version if > 2 min since
    // last edit). The endpoint returns a {action, proposal} envelope.
    const { action: updatedAction, proposal: proposalMeta } =
      await response.json();

    // The server now holds what we SENT (never the live value, which may have
    // moved on — those in-flight edits are still pending in the hook and get
    // flushed by the next write).
    lastPersistedContentRef.current = sentContentStr;

    // Ack the server's (possibly normalized) representation of this save so
    // its round-trip through parent state / cache / refetch is recognized as
    // an echo and never re-applied to the editor.
    ackSavedAction(updatedAction);

    // Keep the proposal detail cache in agreement with the server — no
    // invalidation/refetch, just a surgical in-place patch.
    patchProposalDetailCache(updatedAction, proposalMeta);

    // Always propagate changes to parent, even for in-place updates.
    logger.info('[EmailActionPreview] Propagating changes to parent', {
      oldVersion: action.version_number,
      newVersion: updatedAction.version_number,
      oldId: action.id,
      newId: updatedAction.id,
      versionChanged: updatedAction.version_number !== action.version_number,
      hasInflightEdits:
        JSON.stringify(editedContentRef.current) !== sentContentStr,
    });
    onActionUpdate?.(updatedAction);
  };

  const retryPolicy = useMemo<CoordinatedRetryPolicy>(
    () => ({
      classify: error =>
        error instanceof AutosaveRequestError
          ? { retryable: error.retryable, retryAfterMs: error.retryAfterMs }
          : { retryable: true },
      baseDelayMs: AUTOSAVE_BASE_RETRY_DELAY_MS,
      maxDelayMs: AUTOSAVE_MAX_RETRY_DELAY_MS,
      onRetryScheduled: ({ attempt, delayMs, delaySource }) => {
        logger.warn('[EmailActionPreview] Autosave failed — scheduling retry', {
          actionId: actionIdRef.current,
          proposalId,
          attempt,
          delayMs,
          delaySource,
        });
      },
    }),
    [proposalId]
  );

  // Live content for the approve stale-data check. Read at call time: by the
  // time a footer approves, an autosave may have minted a new action row.
  // Must include context_message when present — the backend hashes it as part
  // of the stored payload (see apps/reasoning/tools/proposal/create.py
  // _build_payload_with_context). Mirror its truthy-only inclusion rule.
  const getLiveContent = useCallback((): LiveActionContent => {
    const payload: Record<string, unknown> = {
      action_params: editedContentRef.current,
    };
    if (contextMessageRef.current) {
      payload.context_message = contextMessageRef.current;
    }
    return {
      actionId: actionIdRef.current,
      executionOrder: executionOrderRef.current,
      payload,
    };
  }, [editedContentRef, contextMessageRef, executionOrderRef]);

  // STALE_ACTIONS recovery is implemented below (it needs the hook's baseline
  // controls); the hook is handed this stable entry point into it.
  const resyncImplRef = useRef<() => void>(() => {});
  const handleResyncFromServer = useCallback(() => {
    resyncImplRef.current();
  }, []);

  const {
    save: saveDraft,
    saveStatus,
    hasPendingChanges,
    syncSavedBaseline,
    markSaved,
    enqueueWrite,
  } = useCoordinatedAutoSave<EmailContent>({
    registrationId: `email-${action.id}`,
    onSave: persistContent,
    initialSavedValue: editedContent,
    // `editable` is also switched off while the editors are held (AI
    // processing, an approve decision in progress). Held is not disabled:
    // those edits must stay pending and flushable, so hold them via `paused`
    // and only disable a preview that can never be edited.
    enabled: !!orgId && (editable || isProcessing),
    paused: isProcessing,
    retry: retryPolicy,
    getContent: getLiveContent,
    onResync: handleResyncFromServer,
  });

  // Adopt `content` as what the server holds without discarding a pending
  // local edit that differs from it (the user's in-flight edits still win).
  const markServerBaseline = useCallback(
    (content: EmailContent, contentStr: string) => {
      lastPersistedContentRef.current = contentStr;
      markSaved(content);
    },
    [markSaved]
  );

  // Apply a genuine external content change (AI edit, version restore) to the
  // editor IN PLACE. Baselines first so the resulting state change never arms
  // a phantom autosave, then pushes the content into the mounted editor via
  // its imperative handle — never a remount.
  const applyExternalContentToEditor = useCallback(
    (newContent: EmailContent, newContentStr: string, updatedAt?: string) => {
      lastPersistedContentRef.current = newContentStr;
      lastAckedServerContentRef.current = newContentStr;
      lastAckedUpdatedAtRef.current = updatedAt ?? null;
      deferredExternalContentRef.current = null;
      syncSavedBaseline(newContent);
      setEditedContent(newContent);
      emailEditorRef.current?.applyExternalContent(newContent);
    },
    [syncSavedBaseline]
  );

  // Force-apply the latest known server content even while the user has
  // unsaved edits. Only fires after the backend has already rejected our
  // stale snapshot (STALE_ACTIONS), so showing the true server content takes
  // priority over preserving local edits that can no longer be saved as-is.
  const resyncFromServer = useCallback(() => {
    const deferred = deferredExternalContentRef.current;
    if (!deferred) {
      return;
    }
    try {
      const liveContentStr = JSON.stringify(editedContentRef.current);
      if (liveContentStr !== deferred.str) {
        logger.warn(
          '[EmailActionPreview] Discarding in-flight edit during STALE_ACTIONS resync',
          { actionId: action.id, proposalId, component: 'proposals' }
        );
      }
    } catch {
      // Best-effort diagnostic — never block the force-apply
    }
    applyExternalContentToEditor(
      deferred.content,
      deferred.str,
      deferred.updatedAt
    );
  }, [applyExternalContentToEditor, editedContentRef, action.id, proposalId]);

  useEffect(() => {
    resyncImplRef.current = resyncFromServer;
  }, [resyncFromServer]);

  // Sync external action content into the editor (AI completed via
  // useProposalRefresh invalidation, version restore, websocket update).
  // NOTE: contentFingerprint is included to detect in-place payload updates
  // (same ID/version) which occur when AI edits within 2 minutes of the last
  // edit (backend time-based versioning).
  //
  // INVARIANT: while the user has UNSAVED EDITS (hasPendingChanges()), NO path
  // in this effect may touch the editor's content — external changes are
  // parked and applied only after the pending save completes or is discarded.
  // Focus alone (no unsaved edit) is NOT sufficient to park; a genuine agent
  // update applies immediately even if the field is focused.
  useEffect(() => {
    const newContent = emailContentFromGmailSend(action);
    const newContentStr = JSON.stringify(newContent);
    const currentContentStr = JSON.stringify(editedContentRef.current);

    const prevActionId = lastActionIdRef.current;
    const idOrVersionChanged =
      action.id !== lastActionIdRef.current ||
      action.version_number !== lastVersionRef.current;

    // Always update refs to track current action
    lastActionIdRef.current = action.id;
    lastVersionRef.current = action.version_number;

    if (newContentStr === currentContentStr) {
      // Content already in sync; on an ID/version rollover just re-baseline.
      if (idOrVersionChanged) {
        markServerBaseline(newContent, newContentStr);
      }
      return;
    }

    // Echo of our own save (the PUT response acked this exact representation,
    // or it matches what we last persisted). Never re-apply it — and never
    // re-baseline to the server representation here, or autosave would see
    // edited !== baseline and arm a phantom save.
    if (
      newContentStr === lastAckedServerContentRef.current ||
      newContentStr === lastPersistedContentRef.current
    ) {
      if (deferredExternalContentRef.current?.str === newContentStr) {
        deferredExternalContentRef.current = null;
      }
      return;
    }

    // Stale refetch racing our PUT: same action but older than the server
    // already acked for our own save. Never let it near the editor.
    if (
      action.id === prevActionId &&
      action.updated_at &&
      lastAckedUpdatedAtRef.current &&
      action.updated_at < lastAckedUpdatedAtRef.current
    ) {
      logger.debug('[EmailActionPreview] Ignoring stale action refetch', {
        actionId: action.id,
        staleUpdatedAt: action.updated_at,
        ackedUpdatedAt: lastAckedUpdatedAtRef.current,
      });
      return;
    }

    // User has UNSAVED edits in flight — park the external content for later.
    // Focus alone (isUserEditing without pending changes) is not enough: a
    // genuine agent update must reach the editor immediately.
    if (hasPendingChanges()) {
      deferredExternalContentRef.current = {
        content: newContent,
        str: newContentStr,
        updatedAt: action.updated_at,
      };
      // Still re-baseline on ID/version rollover so the next autosave compares
      // against the server's latest, not stale data (the user's in-flight
      // edits then win by overwriting server-side, matching prior semantics).
      if (idOrVersionChanged) {
        markServerBaseline(newContent, newContentStr);
      }
      logger.debug(
        '[EmailActionPreview] Deferring external content — user has pending edits',
        { actionId: action.id }
      );
      return;
    }

    // Focused but no pending edits — apply immediately so a genuine agent
    // update is not lost to focus-only parking.
    if (emailEditorRef.current?.isUserEditing()) {
      logger.debug(
        '[EmailActionPreview] Applying external content immediately — focused but no pending edits',
        { actionId: action.id, updatedAt: action.updated_at }
      );
    }

    // Genuine external change — apply in place (idle OR focused with no edits).
    applyExternalContentToEditor(newContent, newContentStr, action.updated_at);
  }, [
    action,
    contentFingerprint,
    applyExternalContentToEditor,
    editedContentRef,
    hasPendingChanges,
    markServerBaseline,
  ]);

  // When the editing session ends (focus left the editor and input went
  // quiet), apply any parked external content — unless our own save has since
  // superseded it, or unsaved local edits still exist (those win; the next
  // save/echo cycle re-evaluates).
  const handleEditingSessionEnd = useCallback(() => {
    const deferred = deferredExternalContentRef.current;
    if (!deferred) {
      return;
    }
    // Only skip applying deferred content if the user still has unsaved edits.
    // (Same guard as the deferral effect: focus alone is not sufficient.)
    if (hasPendingChanges()) {
      return;
    }
    if (
      deferred.updatedAt &&
      lastAckedUpdatedAtRef.current &&
      deferred.updatedAt <= lastAckedUpdatedAtRef.current
    ) {
      // A save we dispatched after this content was parked is newer — drop it.
      deferredExternalContentRef.current = null;
      return;
    }
    logger.debug(
      '[EmailActionPreview] Applying deferred external content after editing session end'
    );
    applyExternalContentToEditor(
      deferred.content,
      deferred.str,
      deferred.updatedAt
    );
  }, [applyExternalContentToEditor, hasPendingChanges]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Once the preview has been opened, keep EmailPreviewModal mounted across
  // subsequent closes so its query cache survives and reopening doesn't
  // re-fetch or flash a loading state. Never reset — the modal only needs to
  // mount once per card's lifetime.
  const hasOpenedPreviewRef = useRef(false);

  const previewPayload = useMemo<EmailPreviewPayload>(
    () => ({
      to: editedContent.to,
      cc: optionalRecipients(editedContent.cc),
      bcc: optionalRecipients(editedContent.bcc),
      subject: editedContent.subject,
      body: editedContent.body,
      body_html: editedContent.body_html,
      social_account_id: editedContent.social_account_id,
      reply_to_message_id: editedContent.reply_to_message_id,
      thread_id: editedContent.thread_id,
      // The typed disposition keeps preview and send in agreement for a
      // user_removed draft — marker-presence inference alone would read it
      // as "none" and preview a signature the send omits.
      signature_disposition: editedContent.signature_disposition,
    }),
    [editedContent]
  );

  const handlePreview = useCallback(() => {
    hasOpenedPreviewRef.current = true;
    setIsPreviewOpen(true);
  }, []);

  const { data: signatureData } = useAccountSignatures(orgId);

  // Remove the signature via the live editor — the identical Plate transform
  // as the block's own remove button, so the change flows through
  // handleContentChange, reconciles to 'user_removed', and autosaves. When no
  // in-draft block exists (a draft whose signature the server appends at send
  // time), there is nothing to delete from the editor, so record the removal
  // directly — 'user_removed' is honoured unconditionally at send.
  const handleRemoveSignatureFromPreview = useCallback(() => {
    const removed = emailEditorRef.current?.removeSignature() ?? false;
    if (!removed) {
      setEditedContent(prev => ({
        ...prev,
        signature_disposition: 'user_removed',
      }));
    }
  }, []);

  // Re-insert the signature after a removal. State goes through
  // setEditedContent so autosave arms (deliberately NOT
  // applyExternalContentToEditor, which baselines the save and would suppress
  // it); the editor is updated in place via its imperative handle. When no
  // signature resolves client-side, fall back to disposition 'none' so the
  // send path appends the server-resolved signature instead.
  const handleAddSignatureBack = useCallback(() => {
    const { current } = editedContentRef;
    const match = selectSignature(signatureData, current.social_account_id);
    if (!match) {
      setEditedContent(prev => ({ ...prev, signature_disposition: 'none' }));
      return;
    }
    const includeDelimiter = match.provider === 'gmail';
    const { body_html: appendedHtml, body: appendedBody } =
      appendSignatureBlock(current, match.html, includeDelimiter);
    setEditedContent(prev => ({
      ...prev,
      body_html: appendedHtml,
      body: appendedBody,
      signature_disposition: 'inserted',
    }));
    emailEditorRef.current?.applyExternalContent({
      ...current,
      body_html: appendedHtml,
      body: appendedBody,
    });
  }, [signatureData, editedContentRef]);

  // Capture the initial disable_tracking value in a ref so it is available
  // inside the initialization effect without making it a reactive dependency.
  const initialDisableTrackingRef = useRef(
    editedContent.disable_tracking ?? false
  );

  // Initialize email tracking context once from action payload and feature flag
  useEffect(() => {
    initializeTracking(trackingEnabled, initialDisableTrackingRef.current);
  }, [initializeTracking, trackingEnabled]);

  // Tracks whether this component has finished its initial alignment with
  // the EmailTrackingProvider. The provider holds tracking preference at
  // the session level — once the user has toggled it on any proposal,
  // every later proposal mounts with `contextDisableTracking` already at
  // a non-default value. The FIRST sync run after the provider is
  // initialized is alignment (provider value → editedContent), NOT a user
  // edit; if it produces a mirror-write we absorb it into the saved
  // baseline so autosave doesn't fire. Every subsequent run is a real
  // user toggle and flows through autosave normally.
  const trackingAlignmentRunRef = useRef(false);

  // Sync tracking state from context (footer toggle) into editedContent for
  // autosave. Gated on `trackingProviderInitialized` so the provider's
  // render-0 default (false) doesn't race with `initializeTracking()` and
  // overwrite editedContent with a stale mirror value.
  useEffect(() => {
    if (!trackingProviderInitialized) {
      return;
    }
    const isAlignmentRun = !trackingAlignmentRunRef.current;
    trackingAlignmentRunRef.current = true;

    const currentDisableTracking =
      editedContentRef.current.disable_tracking ?? false;
    if (currentDisableTracking === contextDisableTracking) {
      // Already in sync — nothing to do.
      return;
    }

    const nextValue: EmailContent = {
      ...editedContentRef.current,
      disable_tracking: contextDisableTracking,
    };
    if (isAlignmentRun) {
      // First post-init sync that produces a mirror-write — alignment,
      // not a user edit. Absorb it into the saved baseline so autosave
      // doesn't fire a phantom save when editedContent updates below.
      markServerBaseline(nextValue, JSON.stringify(nextValue));
    }
    setEditedContent(nextValue);
  }, [
    contextDisableTracking,
    trackingProviderInitialized,
    editedContentRef,
    markServerBaseline,
  ]);

  // Hand every change to the local draft to autosave. The draft is fed by many
  // paths (editor edits, From changes, attachments, signatures, the tracking
  // toggle); the hook ignores values equal to what the server already holds,
  // and holds or drops edits according to pause / lock / freeze.
  const handDraftToAutosave = useEffectEvent((content: EmailContent) => {
    saveDraft(content);
  });
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    handDraftToAutosave(editedContent);
    // handDraftToAutosave is an Effect Event: non-reactive by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedContent]);

  // Handle content changes from EmailEditor
  // Memoized to prevent EmailEditor's debounce effect from re-triggering.
  // Reconcile the signature disposition against the edited body here — the
  // ONE place every user edit flows through — so the state that autosave
  // sends and the approve-time snapshot echoes already carries the verdict
  // the backend would otherwise stamp on the stored payload alone
  // (`reconcile_edited_payload`, edit_source="ui"). Without this the client
  // keeps sending "inserted" after the user deletes the signature block, the
  // server stores "user_removed", and approve fails with 409 STALE_ACTIONS.
  const handleContentChange = useCallback((updates: Partial<EmailContent>) => {
    setEditedContent(prev =>
      reconcileSignatureDisposition({
        ...prev,
        ...updates,
      })
    );
  }, []);

  // Handle From account change (multi-account OAuth)
  const handleFromChange = useCallback((accountId: number) => {
    setEditedContent(prev => ({
      ...prev,
      social_account_id: accountId,
    }));
  }, []);

  // Handle file uploaded
  const handleFileUploaded = useCallback((file: UploadedFile) => {
    setAttachedFiles(prev => [...prev, file]);
    // Add file ID to attachment_file_ids
    setEditedContent(prev => ({
      ...prev,
      attachment_file_ids: [
        ...(prev.attachment_file_ids || []),
        file.id.toString(),
      ],
    }));
  }, []);

  // Handle multiple file attachments - uploads and adds to state
  const handleAttachFiles = useCallback(
    async (files: File[]) => {
      if (!orgId) {
        console.error(
          '[EmailActionPreview] Cannot attach files: orgId not available',
          { actionId: action.id, proposalId, filesCount: files.length }
        );
        toast.error('Unable to attach files. Please try again.');
        return;
      }

      setIsAttaching(true);
      try {
        // Reject files that would push the draft's attachment total over the
        // email cap before any network request — mirrors the backend guard in
        // apps/email/actions/send_email_action_handler.py::MAX_EMAIL_ATTACHMENT_TOTAL_BYTES.
        // Cumulative: seed the running total from the already-attached files,
        // then walk candidates in pick order, so earlier files in one
        // multi-pick can still succeed even if a later one would overflow.
        const existingTotalBytes = attachedFiles.reduce(
          (sum, file) => sum + file.file_size,
          0
        );
        const { accepted: toUpload, rejectedCount: rejectedForSize } =
          filterFilesByAttachmentCap(
            files,
            existingTotalBytes,
            MAX_EMAIL_ATTACHMENT_TOTAL_BYTES
          );

        // Upload the rest in parallel with allSettled (handles partial failures)
        const uploadPromises = toUpload.map(file =>
          fileService.uploadFile(Number(orgId), file)
        );
        const results = await Promise.allSettled(uploadPromises);

        // Process results
        const succeeded: UploadedFile[] = [];
        const failures: { file: File; message: string }[] = [];

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            succeeded.push(result.value);
            handleFileUploaded(result.value);
          } else {
            const { reason } = result;
            // Route through the HTTP-body categorizer only when the error carries
            // both a status AND an actual string response body. Status-bearing
            // errors with no body (e.g. XHR timeout, status 408) fall through to
            // categorizeNetworkError alongside status-less errors, so reason.message
            // is preserved either way instead of collapsing to a generic bucket.
            const info =
              reason instanceof FileUploadError &&
              reason.status !== undefined &&
              typeof reason.response === 'string'
                ? categorizeUploadErrorBody(reason.status, reason.response)
                : categorizeNetworkError(reason);
            failures.push({ file: toUpload[index], message: info.message });
          }
        });

        // Log results
        logger.info('[EmailActionPreview] File upload complete', {
          actionId: action.id,
          proposalId,
          succeeded: succeeded.length,
          failed: failures.length,
          rejectedForSize,
          fileNames: succeeded.map(f => f.original_filename),
        });

        // Show user feedback for candidates rejected by the cumulative cap
        // before any upload was attempted.
        if (rejectedForSize > 0) {
          toast.error(
            `Email attachments are limited to ${MAX_EMAIL_ATTACHMENT_TOTAL_MB} MB total.`
          );
        }

        // Show user feedback for failures
        if (failures.length > 0) {
          console.error('[EmailActionPreview] Some files failed to upload', {
            actionId: action.id,
            proposalId,
            failedFiles: failures.map(f => ({
              name: f.file.name,
              size: f.file.size,
              message: f.message,
            })),
          });

          const failureMessages = failures.map(
            failure =>
              `${failure.file.name || 'File'}: We couldn't attach this file. Try again.`
          );

          if (failures.length <= MAX_INDIVIDUAL_FILE_TOASTS) {
            failureMessages.forEach(message => toast.error(message));
          } else {
            toast.error(`${failures.length} files couldn't be attached.`);
          }
        }
      } catch (error) {
        console.error(
          '[EmailActionPreview] Unexpected error during file upload',
          { actionId: action.id, proposalId, filesCount: files.length, error }
        );
        toast.error(
          'An error occurred while uploading files. Please try again.'
        );
      } finally {
        setIsAttaching(false);
      }
    },
    [orgId, handleFileUploaded, action.id, proposalId, attachedFiles]
  );

  // Handle file removed - updates current action and permanently deletes file
  const handleFileRemoved = useCallback(
    async (fileId: number | string) => {
      if (!orgId || typeof fileId === 'string') {
        return;
      }

      // Save the file object before removing (for potential revert)
      const fileToRemove = attachedFiles.find(f => f.id === fileId);

      try {
        // Remove from local state immediately for responsive UI
        setAttachedFiles(prev => prev.filter(f => f.id !== fileId));

        // Calculate new attachment IDs from action payload (not editedContent)
        const currentIds =
          action.payload.action_params.attachment_file_ids || [];
        const newAttachmentIds = currentIds.filter(
          id => id !== fileId.toString()
        );

        // Build the post-removal content from the live edited content. It
        // also carries any in-flight header/body edits along with the
        // attachment change. This is the OPTIMISTIC snapshot for the
        // immediate UI update and the pre-write baseline below only — the PUT
        // itself sends a fresh snapshot taken inside the queued write (see
        // enqueueWrite below), not this one.
        const nextContent: EmailContent = {
          ...editedContentRef.current,
          attachment_file_ids: newAttachmentIds,
        };
        const nextContentStr = JSON.stringify(nextContent);

        // Baseline BEFORE updating editedContent — not after the request
        // resolves. Autosave sees the draft change this state update causes;
        // baselining only on success would leave a window where it still sees
        // the pre-removal snapshot, decides content has diverged, and arms its
        // own save — a redundant duplicate PUT once this request has already
        // landed. Baselining here first keeps the two in agreement from the
        // same tick.
        markServerBaseline(nextContent, nextContentStr);

        // Update local edited content state
        setEditedContent(nextContent);

        // Update the CURRENT action's payload, on the same serialized write
        // queue as autosave so the two can never race the endpoint (the
        // KeyError-500 / SupersededActionError race, Sentry
        // AHOY-PRODUCTION-103). Reads actionIdRef when the write runs so a
        // rollover that landed via a prior save targets the current row.
        //
        // The snapshot sent below is taken INSIDE the queued write so a write
        // queued behind another captures post-wait content, not the stale
        // pre-wait `nextContent` computed above — otherwise edits typed while
        // this was queued would be silently reverted by this PUT.
        const response = await enqueueWrite(async () => {
          // Derive the attachment IDs from the live content AT EXECUTION TIME,
          // not the pre-wait `newAttachmentIds` snapshot. A write queued behind
          // an in-flight write may run after an upload/removal has already
          // mutated editedContentRef.current; reusing the stale snapshot would
          // clobber those changes. Filter out just this fileId so only the
          // requested removal is applied on top of the current attachments.
          const freshContent: EmailContent = {
            ...editedContentRef.current,
            attachment_file_ids: (
              editedContentRef.current.attachment_file_ids ?? []
            ).filter(id => id !== fileId.toString()),
          };
          const freshContentStr = JSON.stringify(freshContent);

          const res = await fetchWithDefaults(
            `/api/orgs/${orgId}/proposals/${proposalId}/actions/${actionIdRef.current}/update_payload/`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                payload: {
                  action_params: freshContent,
                },
              }),
            }
          );

          if (res.ok) {
            // Endpoint returns a {action, proposal} envelope — unwrap it.
            const { action: updatedAction, proposal: proposalMeta } =
              await res.json();
            // Re-assert the saved baseline against what we actually
            // persisted — the fresh, execution-time content — not the
            // pre-wait `nextContentStr`.
            markServerBaseline(freshContent, freshContentStr);
            ackSavedAction(updatedAction);
            patchProposalDetailCache(updatedAction, proposalMeta);
            onActionUpdate?.(updatedAction);
          }

          return res;
        });

        if (response.ok) {
          // Permanently delete the file from storage
          try {
            await fileService.deleteFile(Number(orgId), fileId);
            logger.info('[EmailActionPreview] File permanently deleted', {
              fileId,
            });
          } catch (deleteError) {
            logger.error(
              '[EmailActionPreview] Failed to delete file:',
              deleteError instanceof Error ? deleteError : undefined,
              {
                actionId: actionIdRef.current,
                proposalId,
                fileId,
              }
            );
          }
        } else {
          logger.error(
            '[EmailActionPreview] Failed to update action payload',
            undefined,
            {
              actionId: actionIdRef.current,
              proposalId,
              fileId,
            }
          );
          // Revert local state on failure - restore the full file object
          if (fileToRemove) {
            setAttachedFiles(prev => [...prev, fileToRemove]);
          }
          setEditedContent(prev => ({
            ...prev,
            attachment_file_ids: [
              ...(prev.attachment_file_ids || []),
              fileId.toString(),
            ],
          }));
        }
      } catch (error) {
        logger.error(
          '[EmailActionPreview] Failed to remove file:',
          error instanceof Error ? error : undefined,
          {
            actionId: actionIdRef.current,
            proposalId,
            fileId,
          }
        );
        // Revert on any error
        if (fileToRemove) {
          setAttachedFiles(prev => [...prev, fileToRemove]);
        }
      }
    },
    [
      orgId,
      proposalId,
      action.payload.action_params,
      onActionUpdate,
      attachedFiles,
      ackSavedAction,
      patchProposalDetailCache,
      enqueueWrite,
      markServerBaseline,
      editedContentRef,
    ]
  );

  return (
    <div>
      <EmailEditor
        ref={emailEditorRef}
        to={editedContent.to}
        cc={editedContent.cc}
        bcc={editedContent.bcc}
        subject={editedContent.subject}
        bodyHtml={editedContent.body_html}
        body={editedContent.body}
        onContentChange={handleContentChange}
        editable={effectiveEditable}
        showGmailBadge={true}
        saveStatus={isProcessing ? 'idle' : saveStatus}
        threadId={editedContent.thread_id}
        onViewThread={
          editedContent.thread_id && onOpenThread ? onOpenThread : undefined
        }
        versionCount={versionCount}
        onShowVersionHistory={onShowVersionHistory}
        connectedAccounts={connectedAccounts}
        selectedAccountId={editedContent.social_account_id}
        onFromChange={handleFromChange}
        orgId={orgId ? parseInt(orgId, 10) : undefined}
        onAttachFile={handleAttachFiles}
        isAttaching={isAttaching}
        resolvedSlots={action.enriched_data?.resolved_slots}
        isReply={editedContent.is_reply}
        embedded={true}
        maxBodyHeight={isSoleAction ? undefined : '300px'}
        onEditingSessionEnd={handleEditingSessionEnd}
        onPreview={effectiveEditable ? handlePreview : undefined}
      />

      {/* File Attachments Display — divider above visually separates the
          attachments list from the email body inside the action card. */}
      {attachedFiles.length > 0 && (
        <div className='border-t border-border-secondary px-5 pt-[9px] pb-4'>
          <div className='text-xs font-medium text-secondary mb-2'>
            Attachments
          </div>
          <div className='space-y-2'>
            {attachedFiles.map(file => {
              const getIcon = () => {
                if (file.mime_type.startsWith('image/')) {
                  return Image01;
                }
                if (
                  file.mime_type.includes('pdf') ||
                  file.file_extension === 'pdf'
                ) {
                  return File02;
                }
                if (
                  file.mime_type.includes('word') ||
                  file.mime_type.includes('document') ||
                  file.file_extension?.match(/docx?/)
                ) {
                  return File04;
                }
                if (
                  file.mime_type.includes('sheet') ||
                  file.mime_type.includes('excel') ||
                  file.file_extension?.match(/xlsx?/)
                ) {
                  return FileCheck02;
                }
                // Default to File02 for all other types
                return File02;
              };
              const Icon = getIcon();

              return (
                <div
                  key={file.id}
                  className='flex items-center gap-3 p-3 rounded-lg border border-primary bg-primary hover:bg-secondary transition-colors'
                >
                  <div className='flex-shrink-0'>
                    <Icon className='w-5 h-5 text-secondary' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <a
                      href={file.download_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sm font-medium text-primary truncate hover:underline'
                    >
                      {file.original_filename}
                    </a>
                    <div className='text-xs text-secondary'>
                      {file.file_size === 0
                        ? '0 B'
                        : (() => {
                            const k = 1024;
                            const sizes = ['B', 'KB', 'MB', 'GB'];
                            const i = Math.floor(
                              Math.log(file.file_size) / Math.log(k)
                            );
                            return `${Math.round((file.file_size / Math.pow(k, i)) * 10) / 10} ${sizes[i]}`;
                          })()}
                    </div>
                  </div>
                  {effectiveEditable && (
                    <button
                      onClick={() => handleFileRemoved(file.id)}
                      className='flex-shrink-0 text-tertiary hover:text-primary transition-colors cursor-pointer'
                      title='Remove attachment'
                    >
                      <svg
                        className='w-4 h-4'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M6 18L18 6M6 6l12 12'
                        />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {orgId !== undefined && hasOpenedPreviewRef.current && (
        <EmailPreviewModal
          isOpen={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          orgId={parseInt(orgId, 10)}
          payload={previewPayload}
          onOpenThread={
            editedContent.thread_id && onOpenThread ? onOpenThread : undefined
          }
          onRemoveSignature={
            effectiveEditable ? handleRemoveSignatureFromPreview : undefined
          }
          onAddSignatureBack={
            effectiveEditable ? handleAddSignatureBack : undefined
          }
        />
      )}
    </div>
  );
}
